import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ProgressBar } from './components/ui/ProgressBar';
import { SlideQuiz } from './components/SlideQuiz';
import { SlideInsight } from './components/SlideInsight';
import { SlideOpportunity } from './components/SlideOpportunity';
import { SlideCalculator } from './components/SlideCalculator';
import { SlideReframe } from './components/SlideReframe';
import { SlideOperatingModel } from './components/SlideOperatingModel';
import { SlideGetStarted } from './components/SlideGetStarted';
import { SlideGoLive } from './components/SlideGoLive';
import { SlideWhatYouGet } from './components/SlideWhatYouGet';
import { SlideWhyItWorks } from './components/SlideWhyItWorks';
import { SlideWhereThingsBreak } from './components/SlideWhereThingsBreak';
import { SlideFrictionToFlow } from './components/SlideFrictionToFlow';
import { SlideComparison } from './components/SlideComparison';
import { SlideComparisonStatic } from './components/SlideComparisonStatic';
import { SlideTimeline } from './components/SlideTimeline';
import { SlideClose } from './components/SlideClose';
export function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1); // 1 for forward, -1 for backward
  const totalSlides = 18;
  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);
  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);
  const goToSlide = useCallback((index: number) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  }, [currentSlide]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const renderSlide = () => {
    switch (currentSlide) {
      case 0:
        return <SlideQuiz headline="How much of Roche Pharma's revenue comes from post-patent brands?" options={['A. ~20%', 'B. ~35%', 'C. ~50%']} correctIndex={1} feedback="Today ~30–40% of revenue comes from post-patent brands → With more products aging out and fewer mega-blockbusters replacing them at the same scale, this share will grow → The revenue tail is getting longer — and larger" buttonLabel="Continue" onContinue={nextSlide} footer="Based on Roche Annual Report 2024" data-id="element-0" />;
      case 1:
        return <SlideOpportunity onContinue={nextSlide} data-id="element-1" />;
      case 2:
        return <SlideQuiz headline="How much marketing optimization do legacy brands typically get?" options={['A. Significant — dedicated teams', 'B. Some — but declining', 'C. Minimal — mostly maintenance']} correctIndex={2} feedback="Biologics don't fall off a cliff overnight — but every percentage point of market share defended is worth enormous value → Even slightly slowing erosion translates into hundreds of millions" buttonLabel="See the impact even a small change can make" onContinue={() => goToSlide(4)} visualHint={<div className="space-y-6" data-id="element-3">
                <div className="text-center mb-4" data-id="element-4">
                  <span className="text-white/50 text-xs uppercase tracking-widest font-medium" data-id="element-5">
                    The attention gap
                  </span>
                </div>
                <div className="flex items-end gap-6 h-52 justify-center" data-id="element-6">
                  <div className="flex flex-col items-center gap-2" data-id="element-7">
                    <div className="w-28 rounded-t-lg relative overflow-hidden bg-teal-400/30 border border-teal-400/50 h-44" data-id="element-8">
                      <div className="absolute bottom-0 w-full h-full bg-gradient-to-t from-teal-400/50 to-transparent" data-id="element-9" />
                      <div className="absolute top-3 w-full text-center text-white/90 text-xs font-bold" data-id="element-10">
                        Billions in revenue
                      </div>
                    </div>
                    <span className="text-white/60 text-xs font-medium uppercase tracking-wider" data-id="element-11">
                      Brand Value
                    </span>
                  </div>
                  <div className="flex flex-col items-center gap-2" data-id="element-12">
                    <div className="w-28 rounded-t-lg relative overflow-hidden bg-amber-400/30 border border-amber-400/50 h-10" data-id="element-13">
                      <div className="absolute bottom-0 w-full h-full bg-gradient-to-t from-amber-400/50 to-transparent" data-id="element-14" />
                    </div>
                    <span className="text-white/60 text-xs font-medium uppercase tracking-wider" data-id="element-15">
                      Optimization
                    </span>
                  </div>
                </div>
                <div className="text-center" data-id="element-16">
                  <span className="text-white/40 text-xs italic" data-id="element-17">
                    How much value is left on the table?
                  </span>
                </div>
              </div>} data-id="element-2" />;
      case 3:
        return <SlideInsight onContinue={nextSlide} data-id="element-18" />;
      case 4:
        return <SlideCalculator onContinue={nextSlide} data-id="element-19" />;
      case 5:
        return <SlideQuiz headline="What would it take to unlock this value?" options={['A. More budget', 'B. More people', 'C. A scalable model', 'D. All it takes is a new approach']} correctIndex={3} feedback="The teams and budgets are focused where they should be — on launches → What's missing is a scalable way to continuously optimize mature brands without adding headcount or complexity" buttonLabel="Continue" onContinue={nextSlide} data-id="element-20" />;
      case 6:
        return <SlideReframe onContinue={nextSlide} data-id="element-21" />;
      case 7:
        return <SlideOperatingModel onContinue={nextSlide} data-id="element-22" />;
      case 8:
        return <SlideGetStarted onContinue={nextSlide} data-id="element-23" />;
      case 9:
        return <SlideGoLive onContinue={nextSlide} data-id="element-24" />;
      case 10:
        return <SlideWhatYouGet onContinue={nextSlide} data-id="element-25" />;
      case 11:
        return <SlideWhyItWorks onContinue={nextSlide} data-id="element-26" />;
      case 12:
        return <SlideWhereThingsBreak onContinue={nextSlide} data-id="element-27" />;
      case 13:
        return <SlideFrictionToFlow onContinue={nextSlide} data-id="element-28" />;
      case 14:
        return <SlideComparison onContinue={nextSlide} data-id="element-29" />;
      case 15:
        return <SlideTimeline onContinue={nextSlide} data-id="element-30" />;
      case 16:
        return <SlideClose data-id="element-31" />;
      case 17:
        return <SlideComparisonStatic data-id="element-32" />;
      default:
        return null;
    }
  };
  return <div className="w-full h-screen bg-navy-800 overflow-hidden relative font-sans text-white" data-id="element-33">
      <ProgressBar current={currentSlide} total={totalSlides} onBack={prevSlide} onSkip={nextSlide} data-id="element-34" />

      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-35">
        <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }} className="absolute inset-0 w-full h-full" data-id="element-36">
          {renderSlide()}
        </motion.div>
      </AnimatePresence>
    </div>;
}